import React, { useState, useMemo, useEffect } from "react";
import { Calculator, Home, DollarSign, Receipt, Save, Send, Trash2, FolderOpen } from "lucide-react";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { useBuyerContent } from "../lib/buyerContent";

const BUYDOWNS = {
  none: { label: "None", steps: [] },
  "3-2-1": { label: "3-2-1", steps: [3, 2, 1] },
  "2-1": { label: "2-1", steps: [2, 1] },
  "1-1": { label: "1-1", steps: [1, 1] },
  "1-0": { label: "1-0", steps: [1, 0] },
};
const UPFRONT = { conventional: 0, fha: 1.75, va: 2.15, usda: 1.0 };

function amort(P, annualPct, n) {
  if (P <= 0 || n <= 0) return 0;
  const r = annualPct / 1200;
  if (r === 0) return P / n;
  return (P * r) / (1 - Math.pow(1 + r, -n));
}
const money = (x) => (isFinite(x) ? x : 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const money0 = (x) => (isFinite(x) ? x : 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function MortgageCalc() {
  const [f, setF] = useState({
    price: 485000, dpMode: "pct", dpPct: 3.5, dpAmt: 16975,
    loanType: "fha", term: 30, rate: 4.5, buydown: "2-1", financeUpfront: true,
    tax: 200, ins: 80, hoa: 0, other: 0, miOverride: "",
    closingFees: 4500, prepaids: 1200, escrows: 900, discountPoints: 0,
    brokerageFee: 0, miscFees: 0, hoaTransfer: 0, lenderCredit: 0, sellerConcessions: 0, earnest: 2000,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const num = (v) => (v === "" || v === null || isNaN(parseFloat(v)) ? 0 : parseFloat(v));

  const r = useMemo(() => {
    const price = num(f.price);
    const down = f.dpMode === "pct" ? price * (num(f.dpPct) / 100) : num(f.dpAmt);
    const baseLoan = Math.max(0, price - down);
    const isGov = f.loanType !== "conventional";
    const upfrontPct = UPFRONT[f.loanType] || 0;
    const upfrontFee = baseLoan * (upfrontPct / 100);
    const financedFee = isGov && f.financeUpfront ? upfrontFee : 0;
    const totalLoan = baseLoan + financedFee;
    const n = num(f.term) * 12;

    const ltv = price > 0 ? (baseLoan / price) * 100 : 0;
    let miPct;
    if (f.miOverride !== "") miPct = num(f.miOverride);
    else if (f.loanType === "fha") miPct = 0.55;
    else if (f.loanType === "usda") miPct = 0.35;
    else if (f.loanType === "va") miPct = 0;
    else miPct = ltv > 95 ? 1.10 : ltv > 90 ? 0.62 : ltv > 80 ? 0.41 : 0;
    const monthlyMI = (totalLoan * (miPct / 100)) / 12;

    const fullPi = amort(totalLoan, num(f.rate), n);
    const steps = BUYDOWNS[f.buydown]?.steps || [];
    const rows = [];
    let buydownCost = 0;
    steps.forEach((step, i) => {
      const yr = i + 1;
      const yRate = Math.max(0, num(f.rate) - step);
      const yPi = amort(totalLoan, yRate, n);
      const savings = fullPi - yPi;
      buydownCost += savings * 12;
      rows.push({ yr, rate: yRate, pi: yPi, savings });
    });
    const escrow = num(f.tax) + num(f.ins) + num(f.hoa) + num(f.other) + monthlyMI;
    const year1Pi = rows.length ? rows[0].pi : fullPi;
    const year1Total = year1Pi + escrow;
    const fullTotal = fullPi + escrow;

    const fees = num(f.brokerageFee) + num(f.miscFees) + num(f.hoaTransfer) + num(f.discountPoints) + buydownCost + num(f.closingFees) + num(f.prepaids) + num(f.escrows);
    const credits = num(f.lenderCredit) + num(f.sellerConcessions) + num(f.earnest);
    const cashToClose = down + fees - credits;

    return { price, down, baseLoan, upfrontFee, financedFee, totalLoan, miPct, monthlyMI, fullPi, rows, buydownCost, escrow, year1Pi, year1Total, fullTotal, fees, credits, cashToClose, isGov, n };
  }, [f]);

  const [scenarios, setScenarios] = useState([]);
  const [lenderOpen, setLenderOpen] = useState(false);
  const [lender, setLender] = useState({ lender_email: "", message: "" });
  const [flash, setFlash] = useState("");
  const [compareIds, setCompareIds] = useState([]);
  const { content } = useContent();
  const { lender: buyerLender } = useBuyerContent();
  const lenderEmail = buyerLender?.email || content?.lender?.email || "";

  const loadScenarios = () => api.get("/me/calc-scenarios").then((res) => setScenarios(res.data.items || [])).catch(() => {});
  // On mount: load saved scenarios and auto-fill the calculator with the most recent run.
  useEffect(() => {
    api.get("/me/calc-scenarios").then((res) => {
      const items = res.data.items || [];
      setScenarios(items);
      if (items[0]?.inputs && Object.keys(items[0].inputs).length) {
        setF((p) => ({ ...p, ...items[0].inputs }));
      }
    }).catch(() => {});
  }, []);

  const toggleCompare = (id) => setCompareIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : (prev.length >= 2 ? [prev[1], id] : [...prev, id]));
  const compareList = compareIds.map((id) => scenarios.find((s) => s.scenario_id === id)).filter(Boolean);

  const buildSummary = () => ({
    purchase_price: money0(r.price), down: money0(r.down),
    loan_type: (f.loanType || "").toUpperCase(), rate: `${num(f.rate)}%`, term: f.term,
    buydown: BUYDOWNS[f.buydown]?.label || "None", total_loan: money(r.totalLoan),
    year1_payment: money(r.year1Total), full_payment: money(r.fullTotal),
    buydown_cost: money(r.buydownCost), cash_to_close: money(r.cashToClose),
  });

  const saveScenario = async () => {
    const name = window.prompt("Name this scenario (e.g. '485k FHA 2-1 buydown')");
    if (name === null) return;
    await api.post("/me/calc-scenarios", { name, inputs: f, summary: buildSummary() }).catch(() => {});
    setFlash("Scenario saved."); setTimeout(() => setFlash(""), 2500);
    loadScenarios();
  };
  const loadScenario = (s) => { if (s.inputs && Object.keys(s.inputs).length) setF({ ...f, ...s.inputs }); };
  const delScenario = async (id) => { await api.delete(`/me/calc-scenarios/${id}`).catch(() => {}); loadScenarios(); };
  const sendToLender = async (e) => {
    e.preventDefault();
    try {
      await api.post("/me/calc/email-lender", { lender_email: lender.lender_email, message: lender.message, summary: buildSummary() });
      setLenderOpen(false); setLender({ lender_email: "", message: "" });
      setFlash("Sent to your lender."); setTimeout(() => setFlash(""), 2500);
    } catch (err) { alert(err?.response?.data?.detail || "Could not send."); }
  };

  const card = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 22 };
  const label = { display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--text)" };
  const grp = { marginBottom: 14 };
  const Inp = ({ k, prefix, suffix, type = "number", ...rest }) => (
    <div style={{ position: "relative" }}>
      {prefix && <span style={{ position: "absolute", left: 12, top: 11, color: "var(--text-muted)", fontSize: 14 }}>{prefix}</span>}
      <input className="input-field" style={{ paddingLeft: prefix ? 26 : 12, paddingRight: suffix ? 30 : 12 }} type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} {...rest} />
      {suffix && <span style={{ position: "absolute", right: 12, top: 11, color: "var(--text-muted)", fontSize: 14 }}>{suffix}</span>}
    </div>
  );
  const Sel = ({ k, opts }) => (
    <select className="input-field" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`calc-${k}`}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  return (
    <div data-testid="mortgage-calculator">
      <span className="overline">Calculators</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, letterSpacing: "-0.02em", margin: "8px 0 12px", lineHeight: 1.1 }}>
        Run the <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>numbers</em>.
      </h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 620, margin: "0 0 32px", lineHeight: 1.6 }}>
        Estimate your monthly payment, temporary buydown savings, and cash to close.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr)", gap: 24, alignItems: "start" }} className="calc-grid">
        {/* INPUTS */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}><Home size={16} color="var(--primary)" /><span className="overline">Property & Loan</span></div>
            <div style={grp}><label style={label}>Purchase price</label><Inp k="price" prefix="$" data-testid="calc-price" /></div>
            <div style={grp}>
              <label style={label}>Down payment</label>
              <div style={{ display: "flex", gap: 8 }}>
                {f.dpMode === "pct" ? <Inp k="dpPct" suffix="%" /> : <Inp k="dpAmt" prefix="$" />}
                <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                  {["pct", "amt"].map((m) => (
                    <button key={m} type="button" onClick={() => set("dpMode", m)} data-testid={`calc-dpmode-${m}`}
                      style={{ padding: "0 14px", border: "none", cursor: "pointer", fontSize: 14, background: f.dpMode === m ? "var(--primary)" : "transparent", color: f.dpMode === m ? "#fff" : "var(--text-muted)" }}>
                      {m === "pct" ? "%" : "$"}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>= {money0(r.down)} down · loan {money0(r.baseLoan)}</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={grp}><label style={label}>Loan type</label><Sel k="loanType" opts={[["conventional", "Conventional"], ["fha", "FHA"], ["va", "VA"], ["usda", "USDA"]]} /></div>
              <div style={grp}><label style={label}>Term</label><Sel k="term" opts={[[15, "15 years"], [20, "20 years"], [25, "25 years"], [30, "30 years"]]} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={grp}><label style={label}>Interest rate</label><Inp k="rate" suffix="%" step="0.001" data-testid="calc-rate" /></div>
              <div style={grp}><label style={label}>Temporary buydown</label><Sel k="buydown" opts={Object.entries(BUYDOWNS).map(([v, o]) => [v, o.label])} /></div>
            </div>
            {r.isGov && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }} data-testid="calc-finance-upfront">
                <input type="checkbox" checked={f.financeUpfront} onChange={(e) => set("financeUpfront", e.target.checked)} />
                Finance the {UPFRONT[f.loanType]}% upfront fee into the loan ({money0(r.upfrontFee)})
              </label>
            )}
          </div>

          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}><DollarSign size={16} color="var(--primary)" /><span className="overline">Monthly Costs</span></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={grp}><label style={label}>Property taxes / mo</label><Inp k="tax" prefix="$" /></div>
              <div style={grp}><label style={label}>Insurance / mo</label><Inp k="ins" prefix="$" /></div>
              <div style={grp}><label style={label}>HOA / mo</label><Inp k="hoa" prefix="$" /></div>
              <div style={grp}><label style={label}>Other / mo</label><Inp k="other" prefix="$" /></div>
            </div>
            <div style={grp}><label style={label}>Mortgage insurance (override)</label><Inp k="miOverride" suffix="% / yr" placeholder={`auto: ${r.miPct}%`} /></div>
          </div>

          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}><Receipt size={16} color="var(--primary)" /><span className="overline">Closing Costs</span></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[["closingFees", "Closing fees"], ["prepaids", "Prepaids"], ["escrows", "Escrows"], ["discountPoints", "Discount points"], ["brokerageFee", "Brokerage fee"], ["miscFees", "Misc fees"], ["hoaTransfer", "HOA transfer"], ["lenderCredit", "Lender credit"], ["sellerConcessions", "Seller concessions"], ["earnest", "Earnest paid"]].map(([k, l]) => (
                <div key={k} style={grp}><label style={label}>{l}</label><Inp k={k} prefix="$" /></div>
              ))}
            </div>
          </div>
        </div>

        {/* RESULTS */}
        <div style={{ position: "sticky", top: 90, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...card, background: "var(--primary)", color: "#fff" }} data-testid="calc-result-year1">
            <span className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>{r.rows.length ? "Year 1 payment" : "Monthly payment"}</span>
            <div className="font-serif" style={{ fontSize: 46, fontWeight: 500, letterSpacing: "-0.02em", margin: "6px 0 4px", lineHeight: 1 }}>{money(r.year1Total)}<span style={{ fontSize: 18, opacity: 0.7 }}>/mo</span></div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
              P&amp;I {money(r.year1Pi)} + taxes/ins/MI {money(r.escrow)}
            </div>
          </div>

          {r.rows.length > 0 && (
            <div style={card} data-testid="calc-buydown-table">
              <span className="overline">Buydown schedule</span>
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12, fontSize: 14 }}>
                <thead><tr style={{ color: "var(--text-muted)", textAlign: "left", fontSize: 12 }}>
                  <th style={{ padding: "6px 4px" }}>Year</th><th>Rate</th><th style={{ textAlign: "right" }}>P&amp;I</th><th style={{ textAlign: "right" }}>Saves/mo</th>
                </tr></thead>
                <tbody>
                  {r.rows.map((row) => (
                    <tr key={row.yr} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={{ padding: "8px 4px" }}>Year {row.yr}</td>
                      <td>{row.rate.toFixed(3)}%</td>
                      <td style={{ textAlign: "right" }}>{money(row.pi)}</td>
                      <td style={{ textAlign: "right", color: "var(--secondary)" }}>{money(row.savings)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "8px 4px" }}>Year {r.rows.length + 1}+</td>
                    <td>{num(f.rate).toFixed(3)}%</td>
                    <td style={{ textAlign: "right" }}>{money(r.fullPi)}</td>
                    <td style={{ textAlign: "right", color: "var(--text-muted)" }}>—</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <span style={{ color: "var(--text-muted)" }}>Total buydown cost</span>
                <strong data-testid="calc-buydown-cost">{money(r.buydownCost)}</strong>
              </div>
            </div>
          )}

          <div style={card} data-testid="calc-loan-summary">
            <span className="overline">Loan summary</span>
            {[["Base loan", money(r.baseLoan)], r.financedFee ? [`Financed upfront fee (${UPFRONT[f.loanType]}%)`, money(r.financedFee)] : null, ["Total loan", money(r.totalLoan)], [`Mortgage insurance (${r.miPct}%)`, `${money(r.monthlyMI)}/mo`], ["Full P&I (after buydown)", `${money(r.fullPi)}/mo`]].filter(Boolean).map(([l, v], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: i ? "1px solid var(--border)" : "none", fontSize: 14 }}>
                <span style={{ color: "var(--text-muted)" }}>{l}</span><span>{v}</span>
              </div>
            ))}
          </div>

          <div style={card} data-testid="calc-cash-to-close">
            <span className="overline">Cash to close</span>
            <div className="font-serif" style={{ fontSize: 32, fontWeight: 500, margin: "6px 0 8px", letterSpacing: "-0.01em" }}>{money(r.cashToClose)}</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Down {money0(r.down)} + costs {money0(r.fees)} − credits {money0(r.credits)}</div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={saveScenario} data-testid="calc-save-btn" className="btn-secondary" style={{ flex: 1, justifyContent: "center" }}>
              <Save size={15} /> Save scenario
            </button>
            <button type="button" onClick={() => { setLender((l) => ({ ...l, lender_email: l.lender_email || lenderEmail })); setLenderOpen(true); }} data-testid="calc-lender-btn" className="btn-primary" style={{ flex: 1, justifyContent: "center" }}>
              <Send size={15} /> Send to lender
            </button>
          </div>
          {flash && <div data-testid="calc-flash" style={{ fontSize: 13, color: "var(--primary)", textAlign: "center" }}>{flash}</div>}

          {scenarios.length > 0 && (
            <div style={card} data-testid="calc-saved-scenarios">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span className="overline">Saved scenarios</span>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Tick two to compare</span>
            </div>
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                {scenarios.map((s) => (
                  <div key={s.scenario_id} data-testid={`calc-scenario-${s.scenario_id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderTop: "1px solid var(--border)", paddingTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      <input type="checkbox" data-testid={`calc-compare-${s.scenario_id}`} checked={compareIds.includes(s.scenario_id)} onChange={() => toggleCompare(s.scenario_id)} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{s.summary?.year1_payment ? `${s.summary.year1_payment}/mo` : ""}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <button type="button" className="btn-ghost" style={{ padding: 7 }} onClick={() => loadScenario(s)} data-testid={`calc-load-${s.scenario_id}`}><FolderOpen size={14} /></button>
                      <button type="button" className="btn-ghost" style={{ padding: 7, color: "#9A4A3A" }} onClick={() => delScenario(s.scenario_id)} data-testid={`calc-del-${s.scenario_id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {compareList.length === 2 && (
            <div style={card} data-testid="calc-compare-panel">
              <span className="overline">Compare</span>
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12, fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "6px 8px 6px 0", color: "var(--text-muted)", fontSize: 11 }}></th>
                    {compareList.map((s) => (
                      <th key={s.scenario_id} style={{ textAlign: "right", padding: "6px 0", fontFamily: "var(--font-serif)", fontSize: 14, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[["year1_payment", "Year 1 / mo"], ["full_payment", "Full / mo"], ["purchase_price", "Price"], ["loan_type", "Type"], ["rate", "Rate"], ["buydown", "Buydown"], ["buydown_cost", "Buydown cost"], ["cash_to_close", "Cash to close"]].map(([k, lbl]) => (
                    <tr key={k} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={{ padding: "8px 8px 8px 0", color: "var(--text-muted)" }}>{lbl}</td>
                      {compareList.map((s) => (
                        <td key={s.scenario_id} style={{ padding: "8px 0", textAlign: "right", fontWeight: 500 }}>{s.summary?.[k] ?? "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5, margin: 0 }}>
            Estimates only. Not a commitment to lend. Actual rates, fees, and MI vary by lender, credit, and program. Confirm everything with your loan officer.
          </p>
        </div>
      </div>

      {lenderOpen && (
        <div onClick={() => setLenderOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(45,45,45,0.5)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={sendToLender} data-testid="calc-lender-modal" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 460, padding: 28 }}>
            <span className="overline">Lender handoff</span>
            <h3 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: "6px 0 6px" }}>Send these numbers</h3>
            <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "0 0 18px", lineHeight: 1.5 }}>We&rsquo;ll email your lender this scenario summary. Replies come straight to you.</p>
            <label style={{ ...label }}>Lender email
              <input required type="email" data-testid="lender-email" className="input-field" style={{ marginTop: 6 }} value={lender.lender_email} onChange={(e) => setLender({ ...lender, lender_email: e.target.value })} placeholder="lender@example.com" />
            </label>
            <label style={{ ...label, marginTop: 14 }}>Message (optional)
              <textarea data-testid="lender-message" className="input-field" style={{ marginTop: 6, minHeight: 80 }} value={lender.message} onChange={(e) => setLender({ ...lender, message: e.target.value })} placeholder="Hi! Here are the numbers we ran — can you confirm?" />
            </label>
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button type="submit" className="btn-primary" data-testid="lender-send-btn" style={{ flex: 1, justifyContent: "center" }}><Send size={15} /> Send</button>
              <button type="button" className="btn-ghost" onClick={() => setLenderOpen(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
